import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  CalendarHeart, MessageCircle, Phone, Mail, Clock, Facebook,
  Send, CheckCircle2, Loader2, Download, CalendarPlus, MapPin, Navigation,
} from "lucide-react";
import { SITE, waLink } from "../lib/site";
import WhatsAppCTABlock from "../components/WhatsAppCTABlock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const consultaTipos = [
  { t: "Consultas presenciales", d: "Atención directa en consultorio." },
  { t: "Consultas en línea", d: "Videoconsulta segura y cómoda." },
  { t: "Atención con cita previa", d: "Tiempo dedicado y sin esperas." },
];

export default function AgendarCita() {
  const [form, setForm] = useState({
    nombre: "", telefono: "", motivo: "", fecha_deseada: "", email: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [createdId, setCreatedId] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim() || !form.telefono.trim() || !form.motivo.trim()) {
      toast.error("Por favor completa nombre, teléfono y motivo de consulta.");
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/appointments`, form);
      setCreatedId(data?.id || null);
      setSent(true);
      toast.success("¡Cita registrada! Te contactaré por WhatsApp.");
      const msg = `Hola Dr. Jairo Franco, soy ${form.nombre}. Quisiera agendar una consulta.
Motivo: ${form.motivo}${form.fecha_deseada ? `\nFecha deseada: ${form.fecha_deseada}` : ""}
Teléfono: ${form.telefono}`;
      setTimeout(() => {
        window.open(waLink(msg), "_blank", "noopener,noreferrer");
      }, 600);
    } catch (err) {
      console.error(err);
      toast.error("No se pudo registrar la cita. Intenta por WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="page-agendar">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              "radial-gradient(700px 500px at 20% 10%, rgba(79,163,255,0.18), transparent 60%), linear-gradient(180deg, #F8FBFF 0%, #EEF3F9 100%)",
          }}
        />
        <div className="mx-auto max-w-7xl px-6 md:px-12 py-16 md:py-24 grid lg:grid-cols-12 gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-brand-medical mb-4">Agendar cita</p>
            <h1 className="font-serif text-5xl md:text-6xl text-brand-deep leading-[1.05]">
              Será un gusto <em className="not-italic text-gradient-blue">acompañarte</em>.
            </h1>
            <p className="mt-6 text-lg text-brand-deep/70 font-light leading-relaxed">
              Déjame tus datos y me pondré en contacto contigo por WhatsApp para
              confirmar tu cita.
            </p>

            {/* Contact rows */}
            <div className="mt-10 space-y-3.5">
              <a
                href={SITE.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-direct"
                className="group flex items-center gap-4 rounded-2xl glass hairline p-4 hover:bg-white transition-colors"
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-brand-bright to-brand-medical flex items-center justify-center text-white">
                  <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-[0.2em] text-brand-medical">WhatsApp</p>
                  <p className="font-serif text-lg text-brand-deep">{SITE.whatsappDisplay}</p>
                </div>
                <Send className="w-4 h-4 text-brand-medical opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
              <a
                href={`tel:+${SITE.whatsappRaw}`}
                data-testid="contact-call"
                className="group flex items-center gap-4 rounded-2xl glass hairline p-4 hover:bg-white transition-colors"
              >
                <div className="w-11 h-11 rounded-xl bg-brand-cloud ring-1 ring-brand-silver flex items-center justify-center text-brand-medical">
                  <Phone className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-[0.2em] text-brand-medical">Teléfono</p>
                  <p className="font-serif text-lg text-brand-deep">{SITE.whatsappDisplay}</p>
                </div>
              </a>
              <a
                href={SITE.facebook}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-facebook"
                className="group flex items-center gap-4 rounded-2xl glass hairline p-4 hover:bg-white transition-colors"
              >
                <div className="w-11 h-11 rounded-xl bg-brand-cloud ring-1 ring-brand-silver flex items-center justify-center text-brand-medical">
                  <Facebook className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-[0.2em] text-brand-medical">Facebook</p>
                  <p className="font-serif text-lg text-brand-deep">@Dr. Jairo Franco</p>
                </div>
              </a>
            </div>

            {/* Ubicaciones */}
            <div className="mt-8 space-y-3" data-testid="locations-list">
              <p className="text-xs uppercase tracking-[0.2em] text-brand-medical flex items-center gap-1.5 mb-1 pl-1">
                <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} /> Consultorios
              </p>
              {SITE.addresses.map((addr, i) => (
                <a
                  key={i}
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr.mapsQuery)}`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid={`contact-location-${i}`}
                  className={`group block rounded-2xl p-5 hairline overflow-hidden relative transition-all ${
                    i === 0
                      ? "bg-gradient-to-br from-brand-deep to-brand-medical text-white premium-shadow hover:premium-shadow-hover"
                      : "bg-white text-brand-deep hover:bg-brand-cloud/40 premium-shadow"
                  }`}
                >
                  {i === 0 && (
                    <div className="absolute -bottom-16 -right-16 w-48 h-48 rounded-full bg-brand-bright/20 blur-2xl" />
                  )}
                  <div className="relative flex items-start gap-4">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      i === 0
                        ? "bg-white/15 ring-1 ring-white/20"
                        : "bg-brand-cloud ring-1 ring-brand-silver"
                    }`}>
                      <MapPin className={`w-5 h-5 ${i === 0 ? "text-white" : "text-brand-medical"}`} strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <p className={`font-serif text-lg leading-tight ${
                        i === 0 ? "text-white" : "text-brand-deep"
                      }`}>
                        {addr.place}
                      </p>
                      <p className={`text-sm font-light mt-1.5 leading-snug ${
                        i === 0 ? "text-white/80" : "text-brand-deep/70"
                      }`}>
                        {addr.street && <>{addr.street}<br /></>}
                        {addr.neighborhood}, {addr.city}
                      </p>
                      <span className={`inline-flex items-center gap-1.5 mt-3 text-xs uppercase tracking-[0.18em] transition-colors ${
                        i === 0
                          ? "text-brand-silver group-hover:text-white"
                          : "text-brand-medical/70 group-hover:text-brand-medical"
                      }`}>
                        Cómo llegar <Navigation className="w-3 h-3" strokeWidth={1.6} />
                      </span>
                    </div>
                  </div>
                </a>
              ))}
            </div>

            {/* Horarios flexibles */}
            <div className="mt-5 rounded-3xl bg-gradient-to-br from-white to-brand-cloud hairline premium-shadow p-6">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-brand-medical" strokeWidth={1.5} />
                <p className="text-xs uppercase tracking-[0.2em] text-brand-medical">Horarios flexibles</p>
              </div>
              <p className="font-serif text-xl text-brand-deep leading-snug mb-2">
                Horarios flexibles para tu comodidad.
              </p>
              <p className="text-sm text-brand-deep/70 font-light leading-relaxed mb-5">
                Contáctame por WhatsApp y encontraremos el horario que mejor se
                adapte a tus necesidades.
              </p>

              <ul className="space-y-2.5 mb-5">
                {consultaTipos.map((c) => (
                  <li key={c.t} className="flex items-start gap-2.5" data-testid={`tipo-${c.t.toLowerCase().replace(/\s+/g, "-")}`}>
                    <CheckCircle2 className="w-4 h-4 text-brand-medical mt-0.5 flex-shrink-0" strokeWidth={1.6} />
                    <div className="flex-1">
                      <p className="font-medium text-brand-deep leading-tight">{c.t}</p>
                      <p className="text-xs text-brand-deep/55 font-light">{c.d}</p>
                    </div>
                  </li>
                ))}
              </ul>

              <a
                href={SITE.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="big-whatsapp-cta"
                className="group relative flex items-center justify-center gap-2.5 w-full rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white px-6 py-4 font-medium tracking-wide transition-all premium-shadow hover:premium-shadow-hover overflow-hidden"
              >
                <span className="absolute inset-0 bg-white/10 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <MessageCircle className="w-5 h-5 relative" strokeWidth={1.6} />
                <span className="relative font-serif text-lg">Agendar por WhatsApp</span>
              </a>
            </div>
          </motion.div>

          {/* FORM */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div className="relative">
              <div className="absolute -inset-3 rounded-[2.5rem] bg-gradient-to-br from-brand-bright/20 to-brand-silver/20 blur-2xl" />
              <form
                onSubmit={onSubmit}
                data-testid="appointment-form"
                className="relative rounded-[2.5rem] glass-strong premium-shadow p-7 md:p-10"
              >
                <h2 className="font-serif text-3xl md:text-4xl text-brand-deep mb-2">
                  Solicita tu consulta
                </h2>
                <p className="text-brand-deep/60 font-light mb-7">
                  Confirmaré tu cita por WhatsApp en cuanto reciba tus datos.
                </p>

                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Nombre completo" required>
                    <input
                      type="text"
                      data-testid="input-nombre"
                      value={form.nombre}
                      onChange={(e) => update("nombre", e.target.value)}
                      placeholder="Tu nombre"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Teléfono / WhatsApp" required>
                    <input
                      type="tel"
                      data-testid="input-telefono"
                      value={form.telefono}
                      onChange={(e) => update("telefono", e.target.value)}
                      placeholder="55 0000 0000"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Correo (opcional)">
                    <input
                      type="email"
                      data-testid="input-email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="tucorreo@ejemplo.com"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Fecha deseada (opcional)">
                    <input
                      type="datetime-local"
                      data-testid="input-fecha"
                      value={form.fecha_deseada}
                      onChange={(e) => update("fecha_deseada", e.target.value)}
                      className={inputCls}
                    />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Motivo de consulta" required>
                      <textarea
                        data-testid="input-motivo"
                        value={form.motivo}
                        onChange={(e) => update("motivo", e.target.value)}
                        placeholder="Cuéntame brevemente el motivo de tu visita"
                        rows={4}
                        className={`${inputCls} resize-none`}
                      />
                    </Field>
                  </div>
                </div>

                <div className="mt-7 flex flex-wrap items-center gap-3">
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="submit-appointment"
                    className="inline-flex items-center gap-2 rounded-full bg-brand-medical hover:bg-brand-deep text-white px-7 py-3.5 font-medium tracking-wide transition-all premium-shadow disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : sent ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : (
                      <CalendarHeart className="w-4 h-4" />
                    )}
                    {sending ? "Enviando..." : sent ? "Cita registrada" : "Agendar cita"}
                  </button>
                  <a
                    href={SITE.bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="form-whatsapp"
                    className="inline-flex items-center gap-2 rounded-full ring-1 ring-brand-medical/40 text-brand-medical hover:bg-white px-7 py-3.5 font-medium tracking-wide transition-all"
                  >
                    <MessageCircle className="w-4 h-4" /> WhatsApp directo
                  </a>
                </div>

                <p className="mt-5 text-xs text-brand-deep/55 font-light">
                  Tus datos se utilizan únicamente para gestionar tu cita y son tratados
                  con absoluta confidencialidad.
                </p>

                {sent && createdId && (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    data-testid="appointment-success"
                    className="mt-6 rounded-2xl bg-gradient-to-br from-brand-cloud to-white hairline p-5"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-50 ring-1 ring-emerald-200 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600" strokeWidth={1.6} />
                      </div>
                      <div className="flex-1">
                        <p className="font-serif text-lg text-brand-deep">¡Cita registrada con éxito!</p>
                        <p className="text-sm text-brand-deep/65 mt-1">
                          Agrega este evento a tu calendario para no olvidarlo.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <a
                            href={`${API}/appointments/${createdId}/ics`}
                            data-testid="download-ics-btn"
                            className="inline-flex items-center gap-2 rounded-full bg-brand-medical hover:bg-brand-deep text-white px-5 py-2.5 text-sm font-medium transition-colors"
                          >
                            <CalendarPlus className="w-4 h-4" /> Agregar a mi calendario
                          </a>
                          <a
                            href={`${API}/appointments/${createdId}/ics`}
                            download
                            data-testid="download-ics-alt"
                            className="inline-flex items-center gap-2 rounded-full ring-1 ring-brand-medical/40 text-brand-medical hover:bg-white px-5 py-2.5 text-sm font-medium transition-colors"
                          >
                            <Download className="w-4 h-4" /> Descargar .ics
                          </a>
                        </div>
                        <p className="mt-3 text-xs text-brand-deep/55">
                          Compatible con Google Calendar, Apple Calendar y Outlook.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </form>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WhatsApp CTAs */}
      <WhatsAppCTABlock variant="dark" highlight="cta-consulta" />
    </div>
  );
}

const inputCls =
  "w-full rounded-2xl bg-white/80 hairline px-4 py-3 text-brand-deep placeholder:text-brand-deep/35 font-light focus:outline-none focus:ring-2 focus:ring-brand-bright/40 focus:border-brand-medical transition-all";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] text-brand-medical mb-2">
        {label} {required && <span className="text-brand-bright">·</span>}
      </span>
      {children}
    </label>
  );
}
